import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  CellClickedEvent,
  themeQuartz,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnPinnedEvent,
  ColumnVisibleEvent,
  GridOptions,
} from "ag-grid-community";
import { ReactElement, useEffect, useMemo, useRef } from "react";
import { client_selected } from "../../client";
import {
  Flex,
  LoadingOverlay,
  Button,
  Text,
  useMantineTheme,
  Loader,
} from "@mantine/core";
import HeaderWithMenu from "./HeaderWithMenu";
import { useTranslation } from "react-i18next";

export interface ActionsColumn<Row = any> {
  colId?: string;
  width?: number;
  pinned?: "left" | "right";
  renderer: (row: Row) => React.ReactNode;
}

export interface DataGridProps<Row = any> {
  title?: string;
  rowData: Row[];
  columnDefs: ColDef<Row>[];
  defaultColDef?: ColDef<Row>;
  isLoading?: boolean;
  height?: number | string;
  pagination?: ReactElement | null;
  onRowClick?: (row: Row) => void;
  storageKey?: string;
  enableColumnPersistence?: boolean;
  context?: any;
  gridOptionsOverride?: Partial<GridOptions<Row>>;
}

const baseTheme = themeQuartz.withParams({
  accentColor: client_selected.colors.primary[0] || "#087AD1",
  backgroundColor: client_selected.colors.basic[0] || "#FFFFFF",
  borderColor: client_selected.colors.basic[2] || "#D7E2E6",
  borderRadius: 2,
  browserColorScheme: "light",
  cellHorizontalPaddingScale: 0.7,
  chromeBackgroundColor: { ref: "backgroundColor" },
  columnBorder: false,
  fontFamily: { googleFont: "Montserrat" },
  fontSize: 14,
  foregroundColor: client_selected.colors.basic[6] || "#555B62",
  headerBackgroundColor: client_selected.colors.other[0] || "#FFFFFF",
  headerFontSize: 14,
  headerFontWeight: 600,
  headerTextColor: client_selected.colors.basic[5] || "#2F2F2F",
  textColor: client_selected.colors.basic[5] || "#2F2F2F",
  rowBorder: true,
  rowVerticalPaddingScale: 0.8,
  sidePanelBorder: true,
  spacing: 6,
  wrapperBorder: false,
  wrapperBorderRadius: 2,
});

export default function AGGridDefault<Row = any>({
  title,
  rowData,
  columnDefs,
  defaultColDef,
  isLoading,
  height = 466,
  pagination,
  onRowClick,
  storageKey,
  enableColumnPersistence = true,
  context,
  gridOptionsOverride,
}: DataGridProps<Row>) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const gridApiRef = useRef<GridApi<Row> | null>(null);

  useEffect(() => {
    if (!gridApiRef.current) return;
    gridApiRef.current.refreshHeader();
  }, [context.query?.orderBy, context.query?.sortOrder, context.hasFilters]);
  

  const saveColumnState = () => {
    if (!enableColumnPersistence || !storageKey) return;
    if (gridApiRef.current) {
      const currentState = gridApiRef.current.getColumnState();
      try {
        localStorage.setItem(storageKey, JSON.stringify(currentState));
      } catch {}
    }
  };

  const restoreColumnState = () => {
    if (!enableColumnPersistence || !storageKey) return;
    if (gridApiRef.current) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return;
        const state = JSON.parse(raw);
        gridApiRef.current.applyColumnState({ state, applyOrder: true });
      } catch {}
    }
  };

  const resetColumnState = () => {
    if (gridApiRef.current) {
      gridApiRef.current.resetColumnState();
    }
    if (enableColumnPersistence && storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {}
    }
  };

  const baseDefaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
      headerComponent: HeaderWithMenu,
      
      cellStyle: {
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        lineHeight: "23px",
        cursor: "pointer",
      },
      ...(defaultColDef || {}),
    }),
    [defaultColDef]
  );

  const onColumnMoved = (event: ColumnMovedEvent) => saveColumnState();
  const onColumnResized = (event: ColumnResizedEvent) => saveColumnState();
  const onColumnPinned = (event: ColumnPinnedEvent) => saveColumnState();
  const onColumnVisible = (event: ColumnVisibleEvent) => saveColumnState();


  const localeText = useMemo(() => ({
    noRowsToShow: t("global.pagination.no_data"),
    loadingOoo: "",
  }), [t]);

  return (
    <>
      <Flex gap={16} w="100%">
        <Flex direction="column" bg="primaryOpacity.1" w="100%">
          <Flex
            bg="primaryOpacity.1"
            justify={"space-between"}
            w="100%"
            p="12px 16px"
          >
            <Text
              fz={{ base: 14 }}
              fw={{ base: 600 }}
              lh={{ base: "32px" }}
              color="primary.0"
            >
              {title}
            </Text>
            <Button
              variant="primaryTransparent"
              fz={{ base: 14 }}
              fw={{ base: 600 }}
              lh={{ base: "32px" }}
              p="0"
              h="32px"
              mih="32px"
              onClick={resetColumnState}
            >
              {t("global.aggrid.restore_table")}
            </Button>
          </Flex>
          <Flex w="100%" style={{ position: "relative" }} direction="column">
            <LoadingOverlay
              visible={!!isLoading}
              overlayBlur={1}
              overlayOpacity={0.5}
              loader={
                <Flex align="center" justify="center" gap="8px">
                  <Loader size="xs" />
                  {t("global.button.loading")}
                </Flex>
              }
            />

            <div
              className="aggrid-center"
              style={{
                height: typeof height === "number" ? `${height}px` : height,
                overflow: "hidden",
                width: "100%",
                position: "relative",
              }}
            >
              <style>
                {`
                  .aggrid-center .ag-cell {
                    display: flex;
                    align-items: center;
                    justify-content: start;
                  }
                `}
              </style>
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={baseDefaultColDef}
                theme={baseTheme}
                rowHeight={40}
                headerHeight={50}
                suppressMultiSort={true}
                localeText={localeText}
                loading={isLoading}
                overlayLoadingTemplate={""}
                suppressDragLeaveHidesColumns={true}
                context={context}
                onGridReady={(params: GridReadyEvent) => {
                  gridApiRef.current = params.api as unknown as GridApi<Row>;
                  if (storageKey) {
                    setTimeout(() => restoreColumnState(), 100);
                  }
                }}
                onCellClicked={(event: CellClickedEvent<any>) => {
                  const colId = event.column.getColId();
                  if (colId.includes("details")) return;
                  if (onRowClick && event.data) onRowClick(event.data as Row);
                }}
                onColumnMoved={onColumnMoved}
                onColumnResized={onColumnResized}
                onColumnPinned={onColumnPinned}
                onColumnVisible={onColumnVisible}
                {...(gridOptionsOverride as any)}
              />
            </div>
            {!!pagination ? (
              <Flex
                bg={theme.colors.basic[0]}
                justify="space-between"
                align="center"
                pt={{ base: 24 }}
                sx={{ borderRadius: "0 0 12px 12px", zIndex: 1000 }}
              >
                {pagination}
              </Flex>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
